<template>
  <div class="user-avatar-container">
    <div v-if="props.src" class="user-avatar-img">
      <img :src="props.src" :style="{ height: `${props.size}px`, width: `${props.size}px` }" />
    </div>
    <div
      v-else-if="firstName !== ''"
      class="user-avatar-text"
      :style="{ fontSize: `${props.size / 2}px`, backgroundColor: props.color, lineHeight: `${props.size}px`, height: `${props.size}px`, width: `${props.size}px` }"
    >
      {{ firstName }}
    </div>
    <div v-else class="user-avatar-img">
      <img src="/accountSdk/statics/default-avatar.png" :style="{ height: `${props.size}px`, width: `${props.size}px` }" />
    </div>
    <div class="edit-btn" v-if="props.canEdit">
      <div>
        <img src="@/assets/icon-camera.svg" v-svg-inline alt="Icon camera" />
      </div>
      <input type="file" :value="selectedFields" title="" @change="handleSelectedAvatar" />
    </div>
    <img v-if="companyUser" class="icon-company" src="/accountSdk/statics/icon-company.png" alt="" />
  </div>
  <el-dialog v-model="dialogShow" width="360px" center :append-to-body="true">
    <div class="cropper-container">
      <div class="cropper-box">
        <!-- 文档:https://github.com/xyxiao001/vue-cropper -->
        <VueCropper
          ref="refCropper"
          :img="cropperImgSrc"
          :autoCrop="true"
          :canScale="false"
          :autoCropWidth="cropperWidth"
          :autoCropHeight="cropperWidth"
          :canMoveBox="false"
          :centerBox="false"
          :fixedBox="true"
          :mode="mode"
          :infoTrue="true"
          :info="false"
          outputType="png"
        ></VueCropper>
      </div>
      <div class="scale-control">
        <span class="minus" @click="handleMinus()">-</span>
        <div style="width: 200px">
          <el-slider v-model="cropperImgScale" :min="100" :max="300" :format-tooltip="formatCropperScale" />
        </div>
        <span class="plus" @click="handlePlus()">+</span>
      </div>
    </div>
    <template #footer>
      <el-button class="save-btn" style="width: 296px" type="primary" :loading="saveBtnLoading" @click="handleSaveImage">{{ $t('Save') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { uploadStream } from '@/api/user/user'
import 'vue-cropper/dist/index.css'
import { VueCropper } from 'vue-cropper'
import { ElMessage } from 'element-plus'
const emits = defineEmits(['on-save'])

const { $t } = getCurrentInstance().proxy
// 有src时使用图片
// 没有src，有nickname时，中文取最后一个字，英文取第一个字母并大写
// 都没有使用默认图片
const props = defineProps({
  size: {
    type: Number,
    default: 60,
  },
  src: String,
  color: String,
  nickname: String,
  companyUser: {
    // 是否是企业用户，如果是，则需要显示企业图标
    type: Boolean,
    default: false,
  },
  canEdit: {
    // 是否可编辑修改，包括裁切
    type: Boolean,
    default: true,
  },
  requstBody: {
    type: Object,
  },
})

const firstName = computed(() => {
  // 包含中文使用最后一位，英文使用第一位，英文都转为大写
  const nickname = (props.nickname || '').trim()
  const isZh = escape(nickname).indexOf('%u') >= 0
  if (isZh) {
    return nickname.substr(-1).toUpperCase()
  }
  return nickname.substr(0, 1).toUpperCase()
})
const refCropper = ref(null)
const dialogShow = ref(false)
const selectedFields = ref(null) // 已选择的图片，关闭窗口是置为null，防止input change事件选择同一张图片时不触发
const saveBtnLoading = ref(false)
const cropperWidth = 248
const imgRealWidth = ref(0)
const imgRealHeight = ref(0)
const mode = ref(`${cropperWidth.value}px ${cropperWidth.value}px`)
const cropperImgScale = ref(100)
const URL = window.URL || window.webkitURL
const formatCropperScale = val => {
  return `${val}%`
}
// 选择后用于裁切的图片
const cropperImgSrc = ref('')

// 选择完图片
const handleSelectedAvatar = e => {
  const file = e.target.files[0]
  const fileExt = file.name.split('.')[file.name.split('.').length - 1].toLowerCase()
  if (!['jpg', 'jpeg', 'png', 'webp'].includes(fileExt)) {
    ElMessage.error({
      message: $t('The avatar must be an image.'),
    })
    return false
  }
  cropperImgSrc.value = URL.createObjectURL(file)
  handleChangeImage()
  selectedFields.value = null
  dialogShow.value = true
}

const handleMinus = () => {
  if (cropperImgScale.value - 100 < 10) {
    cropperImgScale.value = 100
  } else cropperImgScale.value -= 10
}
const handlePlus = () => {
  if (cropperImgScale.value + 10 > 300) {
    cropperImgScale.value = 300
  } else cropperImgScale.value += 10
}

watch(cropperImgScale, (newVal, oldVal) => {
  refCropper.value.changeScale(newVal - oldVal)
})

// 保存图片时
const handleSaveImage = () => {
  saveBtnLoading.value = true
  refCropper.value.getCropData(async data => {
    const requestData = {
      file: data,
      ext: 'png',
      fileType: 'avatar',
    }
    const newRequestData = Object.assign(requestData, props.requstBody)

    uploadStream(newRequestData)
      .then(res => {
        emits('on-ok', res.data)
        dialogShow.value = false
      })
      .finally(() => {
        saveBtnLoading.value = false
      })
  })
}

const handleChangeImage = () => {
  // 更换图片时
  const image = new Image()
  image.src = cropperImgSrc.value
  image.onload = () => {
    imgRealWidth.value = image.width
    imgRealHeight.value = image.height
    const { width, height } = image
    // 图片高度或宽度任意一个小于248，则把那一边置位248，另一边同比例放大
    if (width > height) {
      mode.value = `auto ${cropperWidth}px`
    } else {
      mode.value = `${cropperWidth}px auto`
    }
  }
}
</script>

<style scoped lang="scss">
.user-avatar-container {
  display: inline-flex;
  position: relative;
  .edit-btn {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    border-radius: 100%;
    overflow: hidden;
    opacity: 0;
    transition: 0.5s;
    > div {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      margin: auto;
      background: #00000066;
      cursor: pointer;
      transition: opacity 0.5s;
    }
    input {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }
    &:hover {
      opacity: 1;
    }
  }
  .icon-company {
    position: absolute;
    right: -5px;
    bottom: -5px;
    width: 44%;
    border-radius: 50%;
  }
}
.user-avatar-img {
  display: flex;
  img {
    border-radius: 50%;
    border: 0.5px solid #0000001A;
    box-sizing: border-box;
  }
}
.user-avatar-text {
  text-align: center;
  border-radius: 50%;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);
  color: #fff;
}
.cropper-container {
  width: 296px;
  height: 348px;
  background: #efefef;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  .cropper-box {
    width: 248px;
    height: 248px;
    margin: 24px;
    float: left;
    border-radius: 100%;
    overflow: hidden;
    transform: rotate(0deg);
  }
  .scale-control {
    width: 248px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    > span {
      display: block;
      width: 40px;
      height: 32px;
      line-height: 32px;
      text-align: center;
      font-size: 26px;
      font-weight: bold;
      cursor: pointer;
      position: relative;
      &.minus {
        top: 0;
        left: -4px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .cropper-container {
    width: 100%;
    height: 100%;
    .cropper-box {
      width: 200px;
      height: 200px;
    }
  }

  .save-btn {
    width: 200px !important;
  }
}
</style>
